import React, { useState } from 'react';
import { createContext } from 'use-context-selector';
import { FeedbackNotificationProps } from '@clarke-energia/foton';

import eventTracker from '@services/eventTracking';
import { eventLabels } from '@services/eventTracking/eventsLabels';
import { notifyInfo, notifyError } from '@components/atoms/toast/services';

import { SimulatorTypeEnum } from '@schemas/scenarios-simulator/types';

import { useAuth } from '@auth-wrapper';

import { ICustomClipboardContext } from './types';
import {
  CustomClipboardFeedbackNotificationContents,
  PASTE_FAILURE_MESSAGES,
  PasteFailureReason,
  getPastedLeadName,
  readCustomClipboardValue,
  readFormValuesToPaste,
  readUserClipboard,
  setCustomClipboardValue,
  writeUserClipboard,
  deepClone,
} from './helper';

export const CustomClipboardContext = createContext({} as ICustomClipboardContext);

interface Provider {
  children: React.ReactNode;
}

export const CustomClipboardProvider: React.FC<Provider> = ({ children }: Provider) => {
  const { user } = useAuth();

  const [customClipboardFeedbackNotificationContent, setCustomClipboardFeedbackNotificationContent] =
    useState<FeedbackNotificationProps>();

  const feedbackNotificationsContents = CustomClipboardFeedbackNotificationContents(() =>
    setCustomClipboardFeedbackNotificationContent(undefined),
  );

  function CopyFormValues(formValues?: any, copyFromProposalPreview?: boolean, showFeedback = true) {
    if (formValues) {
      const stringifiedFormValues = JSON.stringify(formValues);

      setCustomClipboardValue(stringifiedFormValues);
      writeUserClipboard(stringifiedFormValues)
        .then(() => {
          showFeedback && setCustomClipboardFeedbackNotificationContent(feedbackNotificationsContents.COPY_SUCCESS);
          notifyInfo('Copiado para a área de transferência');
        })
        .catch(() => {
          showFeedback && setCustomClipboardFeedbackNotificationContent(feedbackNotificationsContents.COPY_ERROR);
          notifyError('Erro ao copiar para a área de transferência');
        });

      eventTracker.trackEvent(
        copyFromProposalPreview
          ? eventLabels.CLICK_COPY_INPUTS_IN_SIMULATION_DESCRIPTION
          : eventLabels.BUTTON_COPYDATA_SIMULATOR,
        {
          user_groups: user?.groups ? user?.groups.join(', ') : '',
          user_role: user?.isSuperAdmin ? 'superadmin' : user?.isAdmin ? 'admin' : 'regular',
        },
      );
    }
  }

  function CopySectionValues(payload?: { fieldsToCopy: string[]; values: Record<string, any> }, showFeedback = true) {
    if (payload) {
      const stringified = JSON.stringify(payload);
      setCustomClipboardValue(stringified);
      writeUserClipboard(stringified)
        .then(() => {
          showFeedback && setCustomClipboardFeedbackNotificationContent(feedbackNotificationsContents.COPY_SUCCESS);
          try {
            notifyInfo('Copiado (seção) para a área de transferência');
          } catch (e) {
            // ignore
          }
        })
        .catch(() => {
          showFeedback && setCustomClipboardFeedbackNotificationContent(feedbackNotificationsContents.COPY_FAILURE);
          try {
            notifyError('Erro ao copiar (seção) para a área de transferência');
          } catch (e) {
            // ignore
          }
        });

      eventTracker.trackEvent(eventLabels.BUTTON_COPYDATA_SIMULATOR, {
        user_groups: user?.groups ? user?.groups.join(', ') : '',
        user_role: user?.isSuperAdmin ? 'superadmin' : user?.isAdmin ? 'admin' : 'regular',
      });
    }
  }

  async function PasteFormValues(
    setInitialValues: React.Dispatch<React.SetStateAction<any>>,
    simulatorType: keyof typeof SimulatorTypeEnum,
    showFeedback = true,
    pasteFromUserClipboard = false,
  ) {
    let failureReason: PasteFailureReason = 'nothing-to-paste';
    try {
      const pasted = await readFormValuesToPaste(pasteFromUserClipboard);

      if (!pasted.values) {
        failureReason = pasted.reason;
        throw new Error(failureReason);
      }

      // Deep clone the parsed form values to ensure all nested structures
      // (like multi-unit scenarios with complex presets) are properly preserved
      const clonedFormValues = deepClone(pasted.values);
      setInitialValues(clonedFormValues);
      showFeedback && setCustomClipboardFeedbackNotificationContent(feedbackNotificationsContents.PASTE_SUCCESS);
      const leadName = getPastedLeadName(clonedFormValues);
      const pastedData = leadName ? `Dados de ${leadName}` : 'Dados';
      try {
        notifyInfo(
          pasted.source === 'in-app-copy'
            ? `${pastedData} da última cópia feita no simulador colados no formulário`
            : `${pastedData} colados no formulário`,
        );
      } catch (e) {
        // ignore
      }
    } catch {
      showFeedback && setCustomClipboardFeedbackNotificationContent(feedbackNotificationsContents.PASTE_FAILURE);
      try {
        notifyError(PASTE_FAILURE_MESSAGES[failureReason]);
      } catch (e) {
        // ignore
      }
    }

    await eventTracker.trackEvent(
      pasteFromUserClipboard ? eventLabels.BUTTON_RECOVERDATA_SIMULATOR : eventLabels.BUTTON_PASTEDATA_SIMULATOR,
      {
        user_groups: user?.groups ? user?.groups.join(', ') : '',
        user_role: user?.isSuperAdmin ? 'superadmin' : user?.isAdmin ? 'admin' : 'regular',
      },
    );
  }

  async function PasteSectionValues(
    applyFn: (relPath: string, value: any) => void,
    allowedFields?: string[] | undefined,
    showFeedback = true,
    pasteFromUserClipboard = false,
  ) {
    try {
      const formValues = pasteFromUserClipboard ? await readUserClipboard() : readCustomClipboardValue();
      const parsedPayload: { fieldsToCopy: string[]; values: Record<string, any> } | null =
        formValues && JSON.parse(formValues);

      if (parsedPayload) {
        const allowed = allowedFields ? new Set(allowedFields) : null;
        for (const rel of parsedPayload.fieldsToCopy) {
          if (allowed && !allowed.has(rel)) continue;
          const value = parsedPayload.values?.[rel];
          try {
            // Deep clone the value to ensure nested structures are properly preserved
            const clonedValue = deepClone(value);
            applyFn(rel, clonedValue);
          } catch (e) {
            // applyFn errors shouldn't break the loop
            // eslint-disable-next-line no-console
            console.warn('PasteSectionValues: applyFn error for', rel, e);
          }
        }

        showFeedback && setCustomClipboardFeedbackNotificationContent(feedbackNotificationsContents.PASTE_SUCCESS);
        try {
          notifyInfo('Conteúdo colado na seção');
        } catch (e) {
          // ignore
        }
      }
    } catch {
      showFeedback && setCustomClipboardFeedbackNotificationContent(feedbackNotificationsContents.PASTE_FAILURE);
      try {
        notifyError('Erro ao colar o conteúdo na seção');
      } catch (e) {
        // ignore
      }
    }

    await eventTracker.trackEvent(
      pasteFromUserClipboard ? eventLabels.BUTTON_RECOVERDATA_SIMULATOR : eventLabels.BUTTON_PASTEDATA_SIMULATOR,
      {
        user_groups: user?.groups ? user?.groups.join(', ') : '',
        user_role: user?.isSuperAdmin ? 'superadmin' : user?.isAdmin ? 'admin' : 'regular',
      },
    );
  }

  React.useEffect(() => {
    setCustomClipboardFeedbackNotificationContent(undefined);
  }, [location.href]);

  return (
    <CustomClipboardContext.Provider
      value={{
        CopyFormValues,
        PasteFormValues,
        CopySectionValues,
        PasteSectionValues,
        customClipboardFeedbackNotificationContent,
      }}
    >
      {children}
    </CustomClipboardContext.Provider>
  );
};
